import { api } from '@/lib/api';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import type {
  DoctorQrCodesResponse,
  QrCodeFilters,
  QrCodeNoteFilters,
  QrCodeNoteResponse,
  QrCodeNotesResponse,
  QrCodesResponse,
  UserQrNoteFilters,
  UserQrNotesRawResponse,
  UserQrNotesResponse,
} from './qrCodeTypes';

export const getAdminQrCodesQuery = (page = 1, filters?: QrCodeFilters) =>
  queryOptions({
    queryKey: queryKeys.adminQrCodes.all(
      page,
      filters as Record<string, unknown> | undefined,
    ),
    queryFn: async () => {
      const searchParams: Record<string, string | number> = { page };

      if (filters?.name) searchParams.name = filters.name;
      if (filters?.type !== undefined) searchParams.type = filters.type;
      if (filters?.doctor_name) searchParams.doctor_name = filters.doctor_name;
      if (filters?.clinic_id) searchParams.clinic_id = filters.clinic_id;
      if (filters?.doctor_id) searchParams.doctor_id = filters.doctor_id;
      if (filters?.date_from) searchParams.date_from = filters.date_from;
      if (filters?.date_to) searchParams.date_to = filters.date_to;
      if (filters?.per_page) searchParams.per_page = filters.per_page;

      const response = await api
        .get('admin/qr-codes', { searchParams })
        .json<QrCodesResponse>();
      return response;
    },
    placeholderData: keepPreviousData,
  });

export const getAdminQrCodeNotesQuery = (
  page = 1,
  filters?: QrCodeNoteFilters,
) =>
  queryOptions({
    queryKey: queryKeys.adminQrCodeNotes.all(
      page,
      filters as Record<string, unknown> | undefined,
    ),
    queryFn: async () => {
      const searchParams: Record<string, string | number> = { page };

      if (filters?.parent_id) searchParams.parent_id = filters.parent_id;
      if (filters?.name) searchParams.name = filters.name;
      if (filters?.doctor_name) searchParams.doctor_name = filters.doctor_name;
      if (filters?.clinic_id) searchParams.clinic_id = filters.clinic_id;
      if (filters?.doctor_id) searchParams.doctor_id = filters.doctor_id;
      if (filters?.date_from) searchParams.date_from = filters.date_from;
      if (filters?.date_to) searchParams.date_to = filters.date_to;
      if (filters?.per_page) searchParams.per_page = filters.per_page;

      const response = await api
        .get('admin/qr-code-notes', { searchParams })
        .json<QrCodeNotesResponse>();
      return response;
    },
    placeholderData: keepPreviousData,
  });

export const getAdminQrCodeNoteQuery = (noteId: number) =>
  queryOptions({
    queryKey: queryKeys.adminQrCodeNotes.single(noteId),
    queryFn: async () => {
      const response = await api
        .get(`admin/qr-code-notes/${noteId}`)
        .json<QrCodeNoteResponse>();
      return response;
    },
  });

export const getAdminUserQrNotesQuery = (
  page = 1,
  filters?: UserQrNoteFilters,
) =>
  queryOptions({
    queryKey: queryKeys.adminUserQrNotes.all(
      page,
      filters as Record<string, unknown> | undefined,
    ),
    queryFn: async () => {
      const searchParams: Record<string, string | number> = { page };

      if (filters?.doctor_id) searchParams.doctor_id = filters.doctor_id;
      if (filters?.per_page) searchParams.per_page = filters.per_page;

      const raw = await api
        .get('admin/user-qr-notes', { searchParams })
        .json<UserQrNotesRawResponse>();
      const response: UserQrNotesResponse = {
        ...raw,
        data: raw.data.map((user) => ({
          id: user.user_id,
          name: user.user_name,
          surname: user.user_surname,
          email: user.user_email,
          phone: user.user_phone,
          created_typeform: user.created_typeform,
          assigned_notes: user.assigned_notes,
        })),
      };
      return response;
    },
    placeholderData: keepPreviousData,
  });

export const getDoctorQrCodesQuery = (doctorId: number) =>
  queryOptions({
    queryKey: queryKeys.doctorQrCodes.all(doctorId),
    queryFn: async () => {
      const response = await api
        .get('admin/qr-codes', { searchParams: { doctor_id: doctorId } })
        .json<DoctorQrCodesResponse>();
      return response;
    },
  });
